import { IOptimizedImage, IRing } from "./interfaces";

const DEFAULT_STORAGE_BUCKET = "ring-swipe.appspot.com";

const normalizeUrl = (url: string | string[] | undefined): string[] => {
  if (!url) return [];
  return Array.isArray(url) ? url : [url];
};

const normalizeStoragePath = (value: string | undefined): string => {
  if (!value) return "";

  try {
    const stripped = value.split("#")[0];
    if (!/^https?:\/\//i.test(stripped) && !stripped.startsWith("gs://")) {
      return decodeURIComponent(stripped.split("?")[0]);
    }

    if (stripped.startsWith("gs://")) {
      const withoutScheme = stripped.slice(5);
      const slashIndex = withoutScheme.indexOf("/");
      return slashIndex === -1 ? "" : decodeURIComponent(withoutScheme.slice(slashIndex + 1));
    }

    const parsed = new URL(stripped);

    if (parsed.hostname === "storage.googleapis.com") {
      const parts = parsed.pathname.split("/").filter(Boolean);
      if (parts.length < 2) return "";
      return decodeURIComponent(parts.slice(1).join("/")).split("?")[0];
    }

    if (parsed.hostname === "firebasestorage.googleapis.com") {
      const parts = parsed.pathname.split("/").filter(Boolean);
      const objectIndex = parts.indexOf("o");
      if (objectIndex === -1) return "";
      return decodeURIComponent(parts[objectIndex + 1] || "").split("?")[0];
    }
  } catch {
    return "";
  }

  return "";
};

const toFirebaseMediaUrl = (path: string): string =>
  `https://firebasestorage.googleapis.com/v0/b/${DEFAULT_STORAGE_BUCKET}/o/${encodeURIComponent(path)}?alt=media`;

const getVariantUrl = (
  entry: IOptimizedImage | undefined,
  preferredWidths: string[],
): string | null => {
  if (!entry) return null;

  for (const width of preferredWidths) {
    const value = entry.variants?.[width];
    if (typeof value === "string" && value) {
      return /^https?:\/\//i.test(value) ? value : toFirebaseMediaUrl(value);
    }
  }

  return null;
};

export const getRingImageUrls = (
  ring: Pick<IRing, "url" | "optimizedImages">,
  preferredWidths: string[] = ["800", "1600", "400"],
): string[] => {
  const urls = normalizeUrl(ring.url);
  if (!ring.optimizedImages?.length) return urls;

  const optimizedMap = new Map<string, IOptimizedImage>();
  for (const entry of ring.optimizedImages) {
    if (entry?.original) {
      optimizedMap.set(normalizeStoragePath(entry.original), entry);
    }
  }

  return urls.map((url) => {
    const optimized = getVariantUrl(optimizedMap.get(normalizeStoragePath(url)), preferredWidths);
    return optimized || url;
  });
};
